import { createAnthropic } from '@ai-sdk/anthropic'
import { createGoogleGenerativeAI } from '@ai-sdk/google'
import { createOpenAI } from '@ai-sdk/openai'
import { customProvider } from 'ai'
import { z } from 'zod'

/** AI providers and their required env vars */

export const aiProviderSchema = z.enum(['openai', 'anthropic', 'gemini'])

export interface AIProviderSpec {
  baseURL: string

  authHeader: {
    name: string
    bearer: boolean
  }

  getTokensUsed: (response: unknown) => number

  // Path to the secret containing the API key in Parameter Store
  apiKeySecret: string
}

export type AIProvider = z.infer<typeof aiProviderSchema>

const providerSpecs = {
  openai: {
    baseURL: 'https://api.openai.com/v1',
    authHeader: {
      name: 'Authorization',
      bearer: true,
    },
    apiKeySecret: '/tunasong/openai/api-key',
    getTokensUsed: (response: unknown) => {
      const schema = z.object({
        usage: z.object({
          total_tokens: z.number(),
        }),
      })
      const parsedResponse = schema.parse(response)
      return parsedResponse.usage.total_tokens
    },
  },
  anthropic: {
    baseURL: 'https://api.anthropic.com/v1',
    authHeader: {
      name: 'x-api-key',
      bearer: false,
    },
    apiKeySecret: '/tunasong/anthropic/api-key',
    getTokensUsed: (response: unknown) => {
      const schema = z.object({
        usage: z.object({
          input_tokens: z.number(),
          output_tokens: z.number(),
        }),
      })
      const parsedResponse = schema.parse(response)
      return parsedResponse.usage.input_tokens + parsedResponse.usage.output_tokens
    },
  },
  gemini: {
    baseURL: 'https://generativelanguage.googleapis.com/v1beta',
    authHeader: {
      name: 'x-goog-api-key',
      bearer: false,
    },
    apiKeySecret: '/tunasong/google/gemini-api-key',
    getTokensUsed: (response: unknown) => {
      const schema = z.object({
        usageMetadata: z.object({
          totalTokenCount: z.number(),
        }),
      })
      const parsedResponse = schema.parse(response)
      return parsedResponse.usageMetadata.totalTokenCount
    },
  },
} satisfies Record<AIProvider, AIProviderSpec & Record<string, unknown>>

export const getProviderByModel = (model: SupportedModel) => {
  for (const provider in supportedModelsByProvider) {
    if (supportedModelsByProvider[provider].includes(model)) {
      return provider as AIProvider
    }
  }
  return null
}

/**
 * Sampling parameters the Anthropic Messages API no longer accepts.
 *
 * These are the Vercel AI SDK's camelCase names; the Anthropic adapter maps
 * them to `temperature` / `top_p` / `top_k` on the wire.
 */
const anthropicUnsupportedSamplingParams = ['temperature', 'topP', 'topK'] as const

/**
 * Drop sampling parameters that the resolved provider rejects.
 *
 * `temperature`, `top_p` and `top_k` are **removed** on current Anthropic
 * models (Claude Opus 4.7 and later, Claude Sonnet 5) — sending any of them
 * returns a 400. Our request schemas carry `.default()`s
 * (`chat.schema.ts`, `text-params.ts`), so `temperature` is present on every
 * outbound request whether or not a caller asked for one.
 *
 * Applied here, at the single outbound chokepoint every text call passes
 * through, rather than at each of the five call sites. The fields stay on the
 * public request schemas: OpenAI and Gemini still accept them, and only the
 * Anthropic request omits them.
 *
 * Provider-conditional, not model-conditional, on purpose: it must stay
 * correct across Anthropic id bumps without a second table to keep in sync,
 * and Anthropic's own guidance is to steer behaviour by prompting instead.
 */
export const stripUnsupportedSamplingParams = <T extends object>(model: SupportedModel, params: T): T => {
  if (getProviderByModel(model) !== 'anthropic') {
    return params
  }
  const dropped: readonly string[] = anthropicUnsupportedSamplingParams
  return Object.fromEntries(Object.entries(params).filter(([key]) => !dropped.includes(key))) as T
}

export const getMinuteCostByTranscriptionModel = (model: SupportedTranscribeModel) => transcribeModelCost[model]

export const getTokenCostByModel = (model: SupportedModel) => aiModelTokenCost[model]

export const getProviderSpec = (provider: AIProvider, type: 'client' | 'server' = 'server') => {
  const spec = providerSpecs[provider]
  if (type === 'client') {
    return {
      ...spec,
      baseURL: `https://tunasong.com/stream/proxy`,
    }
  }
  return spec
}

export const getProviderApiKeySecret = (provider: AIProvider) => providerSpecs[provider]?.apiKeySecret

const createProxyFetch = ({ model, authToken }: { model: SupportedModel; authToken: string }) => {
  /** Proxy fetch to our API   */
  const proxyFetch: typeof fetch = async (input: string | Request | URL, options: RequestInit = {}) => {
    // Send the body, headers in the body. We do this to preserve the headers
    // and avoid CORS issues
    const url = typeof input === 'string' ? input : input.toString()

    const { headers, body } = input instanceof Request ? input : options

    // Remove the authorization header from the request

    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        model,
        provider: getProviderByModel(model),
        payload: body,
        headers,
      }),
    })
  }
  return proxyFetch
}

/** Custom provider. authToken is JWT token in client mode, and API key in server mode */
const getAIModelInternal = ({
  type,
  authToken,
  model,
}: {
  type: 'client' | 'server'
  authToken: string
  model: SupportedModel
}) => {
  const proxyFetch = createProxyFetch({ model, authToken })
  const baseURL = type === 'client' ? 'https://tunasong.com/stream/proxy' : undefined
  const apiKey = type === 'server' ? authToken : 'NOT_USED'

  /** Create custom providers with proxyFetch */
  const openai = createOpenAI({
    baseURL,
    apiKey,
    fetch: type === 'client' ? proxyFetch : fetch,
  })
  const anthropic = createAnthropic({
    baseURL,
    fetch: type === 'client' ? proxyFetch : fetch,
    apiKey,
  })
  const gemini = createGoogleGenerativeAI({
    baseURL,
    fetch: type === 'client' ? proxyFetch : fetch,
    apiKey,
  })

  const transcribeModels = {
    'whisper-1': openai.transcription('whisper-1'),
    'gpt-4o-transcribe': openai.transcription('gpt-4o-transcribe'),
    'gpt-4o-mini-transcribe': openai.transcription('gpt-4o-mini-transcribe'),
  }

  /**
   * ADD YOUR MODELS HERE
   */
  const languageModels = {
    // OpenAI models
    'gpt-5.5': openai('gpt-5.5'),
    'gpt-5.4': openai('gpt-5.4'),
    'gpt-5.4-mini': openai('gpt-5.4-mini'),
    'gpt-4o-audio-preview': openai('gpt-4o-audio-preview'),
    // Anthropic models
    // https://platform.claude.com/docs/en/about-claude/models/overview
    'claude-opus-4-7': anthropic('claude-opus-4-7'),
    'claude-opus-4-6': anthropic('claude-opus-4-6'),
    'claude-sonnet-4-6': anthropic('claude-sonnet-4-6'),
    'claude-sonnet-4-5': anthropic('claude-sonnet-4-5'),
    // Google Gemini models
    'gemini-2.5-flash': gemini('gemini-2.5-flash'),
    'gemini-2.5-pro': gemini('gemini-2.5-pro'),
  }

  const provider = customProvider({
    languageModels,

    fallbackProvider: openai,
  })
  return { provider, openai, anthropic, gemini, languageModels, transcribeModels }
}

export const getAIModel = ({
  type,
  authToken,
  model,
}: {
  type: 'client' | 'server'
  authToken: string
  model: SupportedModel
}) => {
  const { provider } = getAIModelInternal({ type, authToken, model })
  return provider.languageModel(model)
}

export const getTranscriptionModel = ({
  type,
  authToken,
  model,
}: {
  type: 'client' | 'server'
  authToken: string
  model: SupportedTranscribeModel
}) => {
  const { openai } = getAIModelInternal({ type, authToken, model })

  const transcriptionModel = openai.transcription(model)
  return transcriptionModel
}

// The cost is per minute of transcription
export const transcribeModelCost = {
  'whisper-1': 100,
  // @todo gpt-4o requires mp3 or wav
  // 'gpt-4o-transcribe': 100,
}

export type SupportedTranscribeModel = keyof typeof transcribeModelCost

interface TokenPrice {
  inputPerMTok: number
  outputPerMTok: number
}

/**
 * Published list prices, USD per million tokens (standard tier, global
 * routing, short-context rate, no batch or cache discount). Recorded so the
 * weights in `aiModelTokenCost` can be re-derived, and checked by
 * `providers.test.ts`, from the providers' own pricing pages rather than from
 * memory. Prices as of 2026-10-07:
 *
 * - OpenAI:    https://developers.openai.com/api/docs/pricing
 * - Anthropic: https://platform.claude.com/docs/en/about-claude/pricing
 * - Google:    https://ai.google.dev/gemini-api/docs/pricing
 *
 * `gpt-4o-audio-preview` is deliberately absent: OpenAI does not list it on its
 * pricing page, so its weight is carried, not derived.
 */
export const modelTokenPrices = {
  'gpt-5.5': { inputPerMTok: 5, outputPerMTok: 30 },
  'gpt-5.4': { inputPerMTok: 2.5, outputPerMTok: 15 },
  'gpt-5.4-mini': { inputPerMTok: 0.75, outputPerMTok: 4.5 },
  'claude-opus-4-7': { inputPerMTok: 5, outputPerMTok: 25 },
  'claude-opus-4-6': { inputPerMTok: 5, outputPerMTok: 25 },
  'claude-sonnet-4-6': { inputPerMTok: 3, outputPerMTok: 15 },
  'claude-sonnet-4-5': { inputPerMTok: 3, outputPerMTok: 15 },
  'gemini-2.5-flash': { inputPerMTok: 0.3, outputPerMTok: 2.5 },
  'gemini-2.5-pro': { inputPerMTok: 1.25, outputPerMTok: 10 },
} as const satisfies Record<string, TokenPrice>

export type PricedModel = keyof typeof modelTokenPrices

/**
 * Share of a billed token assumed to be input. `updateTokenQuota` only sees
 * `totalTokens`, so a weight has to assume a mix. Agent turns dominate usage
 * and are input-heavy: every step resends the preamble, entity context and tool
 * schemas (several thousand tokens) and gets back a few hundred tokens of text
 * or a tool call. 90/10 matches that shape. Plain chat sits closer to 80/20,
 * which would move no weight by more than about 10%.
 */
export const quotaInputShare = 0.9

/** The anchor: `claude-sonnet-4-6` keeps its historical weight, so Anthropic users' quota does not move. */
export const quotaAnchorModel: PricedModel = 'claude-sonnet-4-6'
export const quotaAnchorWeight = 2

const blendedPricePerMTok = ({ inputPerMTok, outputPerMTok }: TokenPrice) =>
  quotaInputShare * inputPerMTok + (1 - quotaInputShare) * outputPerMTok

/**
 * The quota weight formula, rounded to two decimals:
 *
 *   blended(m) = 0.9 × inputPrice(m) + 0.1 × outputPrice(m)
 *   weight(m)  = anchorWeight × blended(m) / blended(claude-sonnet-4-6)
 *
 * One weighted token is then worth the same number of dollars whichever model
 * spent it ($2.10 per million weighted tokens at the sonnet anchor).
 */
export const quotaWeightFromPrice = (price: TokenPrice) =>
  Math.round(
    ((quotaAnchorWeight * blendedPricePerMTok(price)) / blendedPricePerMTok(modelTokenPrices[quotaAnchorModel])) * 100
  ) / 100

/**
 * This also defines the models.
 *
 * These are *relative* quota weights consumed by `updateTokenQuota`
 * (`usage.aiTokens += totalTokens × weight`), not prices. Each token-billed
 * model with a published price has the weight
 * `quotaWeightFromPrice(modelTokenPrices[model])`. The literals stay so the
 * table reads at a glance, and `providers.test.ts` fails if one drifts from its
 * recorded price.
 *
 * Exceptions, which are not token-billed or have no published price:
 * - `whisper-1`: transcription is billed per minute (`transcribeModelCost`).
 * - `gpt-4o-audio-preview`: no published price, so the slot's existing weight
 *   is carried rather than invented.
 * - `elevenlabs-music-*`: billed per second of audio (see music-task.ts).
 *
 * Image generation does not use this table; see `imageGenerationQuotaWeight`.
 */
export const aiModelTokenCost = {
  'whisper-1': 0.006,
  'gpt-5.5': 3.57,
  'gpt-5.4': 1.79,
  'gpt-5.4-mini': 0.54,
  'gpt-4o-audio-preview': 5,
  'claude-opus-4-7': 3.33,
  'claude-opus-4-6': 3.33,
  'claude-sonnet-4-6': 2,
  'claude-sonnet-4-5': 2,
  'gemini-2.5-flash': 0.25,
  'gemini-2.5-pro': 1.01,
  // v2 is the current default (introducing-music-v2).
  'elevenlabs-music-v1': 100,
  'elevenlabs-music-v2': 100,
}

/**
 * Quota weight per token of an image-generation Responses call. It is frozen at
 * the weight images were billed at before the token weights were re-derived
 * from prices (the old `gpt-5.4` weight of 12, GH #1958), so that the
 * re-derivation does not make images about 7x cheaper as a side effect.
 *
 * Image cost is driven by the `image_generation` tool, not by the chat model
 * that orchestrates the call. gpt-image-1.5 lists $5 per MTok for text input
 * and $32 for image output. Under the same sonnet anchor, an output-dominated
 * image call lands at roughly 12 to 15, so 12 is in line with the formula
 * rather than a subsidy. It is not derived from it, though: the Responses
 * `usage.total_tokens` mixes orchestrator and image tokens in proportions we
 * do not see.
 */
export const imageGenerationQuotaWeight = 12

export type SupportedModel = keyof typeof aiModelTokenCost

/**
 * ElevenLabs music-generation model ids (as sent to the ElevenLabs API), kept
 * here in the browser-safe registry so both the Lambda worker and the client
 * RTK query can share the union without pulling in the node-only SDK wrapper.
 * v2 adds better vocals/arrangement, multilingual, and section-by-section
 * composition — see https://elevenlabs.io/blog/introducing-music-v2.
 */
export const elevenLabsMusicModels = ['music_v1', 'music_v2'] as const
export type ElevenLabsMusicModel = (typeof elevenLabsMusicModels)[number]

export const defaultElevenLabsMusicModel: ElevenLabsMusicModel = 'music_v2'

/** Map an ElevenLabs music model id to its internal quota model key. */
export const musicQuotaModel = (modelId: ElevenLabsMusicModel): SupportedModel =>
  modelId === 'music_v2' ? 'elevenlabs-music-v2' : 'elevenlabs-music-v1'

const supportedModelsByProvider: Record<string, [SupportedModel, ...SupportedModel[]]> = {
  openai: [
    'gpt-5.5',
    'gpt-5.4',
    'gpt-5.4-mini',
    'gpt-4o-audio-preview',
    // 'whisper-1',
  ],
  anthropic: ['claude-opus-4-7', 'claude-opus-4-6', 'claude-sonnet-4-6', 'claude-sonnet-4-5'],
  gemini: ['gemini-2.5-flash', 'gemini-2.5-pro'],
}

const aiModelNames: SupportedModel[] = Object.keys(aiModelTokenCost) as SupportedModel[]

/** Chat/language models selectable in the UI (excludes transcription and music-only models) */
const selectableModels = aiModelNames.filter(
  m => !['whisper-1', 'elevenlabs-music-v1', 'elevenlabs-music-v2'].includes(m)
) as [SupportedModel, ...SupportedModel[]]

export const aiModelsSchema = z.enum(selectableModels)
